import apiClient from './api.service';
import type { RejectionPayload } from './rejection.service';

export interface MaterialRequestItem {
  id?: string;
  item_id: string;
  item_code?: string;
  item_name?: string;
  requested_quantity: number;
  base_quantity?: number;
  area_dimension?: number;
  /** Measurement snapshot: base_quantity pieces × dimensions (mm) → area_dimension factor */
  measurement_type?: string | null;
  length_mm?: number | string | null;
  width_mm?: number | string | null;
  height_mm?: number | string | null;
  /** Current item master measurement (hydrated on getById) */
  item_measurement_type?: string | null;
  profile_length?: string | null;
  default_width_mm?: number | string | null;
  default_height_mm?: number | string | null;
  issued_quantity: number;
  pending_quantity: number;
  /** Soft-reserved qty on MR approve (Wave soft-reserve) */
  reserved_quantity?: number;
  uom: string;
  estimated_unit_cost?: number;
  estimated_total_cost?: number;
  bom_reference?: boolean;
  notes?: string;
  /** Hydrated from item master for heat-tracked issuance */
  is_critical?: boolean;
  heat_number_mandatory?: boolean;
  heat_number?: string;
  dimension?: string;
  work_order_line_item_id?: string | null;
  boq_line_item_id?: string | null;
  /** Display label when hydrated (WO line # + description). */
  boq_line_label?: string | null;
}

export interface MaterialRequest {
  id: string;
  request_number: string;
  company_id: string;
  work_order_id?: string | null;
  /** Set by API from work_orders.work_order_number */
  work_order_number?: string | null;
  project_id?: string | null;
  context_kind?: 'WorkOrder' | 'Project' | 'MaintenanceCase' | 'Internal';
  project_summary?: {
    id: string;
    project_number?: string;
    project_name?: string;
  };
  context_label?: string;
  reference_type?: string | null;
  reference_id?: string | null;
  request_date: string;
  required_date?: string;
  request_type: 'BOM' | 'Manual' | 'Mixed' | 'Tools';
  request_status: 'Draft' | 'Pending' | 'Approved' | 'Rejected' | 'Partially Issued' | 'Fully Issued' | 'Closed' | 'Cancelled';
  approval_status: 'Not Required' | 'Pending' | 'Approved' | 'Rejected';
  approval_instance_id?: string | null;
  total_estimated_cost: number;
  priority: 'Low' | 'Normal' | 'High' | 'Urgent';
  source_warehouse_id?: string;
  department_id?: string | null;
  requested_by: string;
  approved_by?: string;
  approved_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  items: MaterialRequestItem[];
  issuances?: any[];
}

export interface CreateMaterialRequestDTO {
  context_kind?: 'WorkOrder' | 'Project' | 'MaintenanceCase' | 'Internal';
  work_order_id?: string | null;
  project_id?: string | null;
  /** Maintenance / non-WO reference (e.g. AssetMaintenanceCase) */
  reference_type?: string | null;
  reference_id?: string | null;
  request_type: 'BOM' | 'Manual' | 'Mixed' | 'Tools';
  required_date?: string;
  priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
  source_warehouse_id?: string;
  department_id?: string;
  notes?: string;
  items: Array<{
    item_id: string;
    item_code?: string;
    item_name?: string;
    area_dimension?: number;
    length_mm?: number | null;
    width_mm?: number | null;
    height_mm?: number | null;
    requested_quantity: number;
    uom: string;
    estimated_unit_cost?: number;
    bom_reference?: boolean;
    notes?: string;
    heat_number?: string;
    dimension?: string;
    work_order_line_item_id?: string | null;
    boq_line_item_id?: string | null;
  }>;
}

export interface MaterialRequestLineDimensions {
  length_mm?: number | null;
  width_mm?: number | null;
  height_mm?: number | null;
}

export interface InventoryAvailability {
  item_id: string;
  available_quantity: number;
  free_quantity?: number;
  on_hand_quantity?: number;
  is_sufficient: boolean;
  requires_heat_dimension?: boolean;
  heat_number?: string;
  dimension?: string;
}

/** Scaled BOQ material line for preview / partial MR create */
export interface BoqEstimationPreviewLine {
  line_key: string;
  /** Null when BOQ material has no inventory link yet. */
  item_id: string | null;
  boq_material_id?: string;
  item_code?: string;
  item_name?: string;
  requested_quantity: number;
  uom: string;
  estimated_unit_cost?: number;
  notes?: string;
  needs_inventory_link?: boolean;
  work_order_line_item_id?: string | null;
  boq_line_item_id?: string | null;
  boq_line_label?: string | null;
}

class MaterialRequestService {
  async getAll(filters?: {
    status?: string;
    priority?: string;
    startDate?: string;
    endDate?: string;
    project_id?: string;
    context_kind?: string;
    work_order_id?: string;
    fulfillmentQueue?: boolean;
  }): Promise<MaterialRequest[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.project_id) params.append('project_id', filters.project_id);
      if (filters?.context_kind) params.append('context_kind', filters.context_kind);
      if (filters?.work_order_id) params.append('work_order_id', filters.work_order_id);
      if (filters?.fulfillmentQueue) params.append('fulfillmentQueue', 'true');

      const response = await apiClient.get(`/inventory/material-requests?${params.toString()}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching material requests:', error);
      return [];
    }
  }

  async getById(id: string): Promise<MaterialRequest> {
    const response = await apiClient.get(`/inventory/material-requests/${id}`);
    return response.data;
  }

  async getByWorkOrder(workOrderId: string): Promise<MaterialRequest[]> {
    try {
      const response = await apiClient.get(`/inventory/material-requests/work-order/${workOrderId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching material requests by work order:', error);
      return [];
    }
  }

  async create(data: CreateMaterialRequestDTO): Promise<MaterialRequest> {
    const response = await apiClient.post('/inventory/material-requests', data);
    return response.data;
  }

  async createFromBOM(
    workOrderId: string,
    body?: {
      warehouse_id?: string;
      bom_id?: string;
      bom_line_ids?: string[];
      required_date?: string;
      priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
      notes?: string;
      department_id?: string;
      bom_line_traceability?: Record<string, { heat_number?: string; dimension?: string }>;
      bom_line_area_dimension?: Record<string, number>;
      bom_line_dimensions?: Record<string, MaterialRequestLineDimensions>;
      work_order_line_by_bom_line_id?: Record<string, string>;
    }
  ): Promise<MaterialRequest> {
    const response = await apiClient.post(`/inventory/material-requests/from-bom/${workOrderId}`, body ?? {});
    return response.data;
  }

  async previewBoqEstimation(
    workOrderId: string,
    options?: { production_unit_number?: number | null }
  ): Promise<BoqEstimationPreviewLine[]> {
    const params =
      options?.production_unit_number != null && options.production_unit_number !== undefined
        ? { production_unit_number: options.production_unit_number }
        : undefined;
    const response = await apiClient.get(
      `/inventory/material-requests/boq-estimation-preview/${workOrderId}`,
      { params }
    );
    return response.data ?? [];
  }

  async createFromBoqEstimation(
    workOrderId: string,
    body?: {
      warehouse_id?: string;
      required_date?: string;
      priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
      notes?: string;
      department_id?: string;
      line_keys?: string[];
      /** Inventory picks for BOQ materials that were description-only. */
      item_id_by_line_key?: Record<string, string>;
      boq_line_traceability?: Record<string, { heat_number?: string; dimension?: string }>;
      boq_line_area_dimension?: Record<string, number>;
      boq_line_dimensions?: Record<string, MaterialRequestLineDimensions>;
      production_unit_number?: number | null;
      work_order_line_by_line_key?: Record<string, string>;
    }
  ): Promise<MaterialRequest> {
    const response = await apiClient.post(
      `/inventory/material-requests/from-boq-estimation/${workOrderId}`,
      body ?? {}
    );
    return response.data;
  }

  async update(id: string, data: Partial<CreateMaterialRequestDTO>): Promise<MaterialRequest> {
    const response = await apiClient.put(`/inventory/material-requests/${id}`, data);
    return response.data;
  }

  async submitForApproval(id: string): Promise<MaterialRequest> {
    const response = await apiClient.post(`/inventory/material-requests/${id}/submit`);
    return response.data;
  }

  async approve(id: string, remarks?: string): Promise<MaterialRequest> {
    const response = await apiClient.post(`/inventory/material-requests/${id}/approve`, { remarks: remarks ?? undefined });
    return response.data;
  }

  async reject(id: string, payload: RejectionPayload): Promise<MaterialRequest> {
    const response = await apiClient.post(`/inventory/material-requests/${id}/reject`, payload);
    return response.data;
  }

  async cancel(id: string): Promise<MaterialRequest> {
    const response = await apiClient.post(`/inventory/material-requests/${id}/cancel`);
    return response.data;
  }

  async checkInventoryAvailability(
    items: Array<{
      item_id: string;
      requested_quantity: number;
      warehouse_id?: string;
      heat_number?: string;
      dimension?: string;
    }>
  ): Promise<InventoryAvailability[]> {
    try {
      const body = (await apiClient.post('/inventory/material-requests/check-availability', { items })) as unknown;
      if (Array.isArray(body)) {
        return body as InventoryAvailability[];
      }
      if (body && typeof body === 'object' && Array.isArray((body as { data?: unknown }).data)) {
        return (body as { data: InventoryAvailability[] }).data;
      }
      if (
        body &&
        typeof body === 'object' &&
        (body as { data?: { data?: unknown } }).data &&
        Array.isArray((body as { data: { data: InventoryAvailability[] } }).data.data)
      ) {
        return (body as { data: { data: InventoryAvailability[] } }).data.data;
      }
      return [];
    } catch (error) {
      console.error('Error checking inventory availability:', error);
      return items.map(item => ({
        item_id: item.item_id,
        available_quantity: 0,
        is_sufficient: false,
      }));
    }
  }
}

export const materialRequestService = new MaterialRequestService();
export default materialRequestService;
